import type { LoginResponse } from '@greatsumini/react-facebook-login';
import ReactFacebookLogin from '@greatsumini/react-facebook-login';
import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useFetcher } from 'react-router';
import { ClientOnly } from 'remix-utils/client-only';
import type { FetcherData } from 'types';

import FacebookIcon from '~/assets/icons/Facebook';
import RegisterIntentEvent from '~/events/RegisterIntentEvent';
import useEnv from '~/hooks/useEnv';
import useRootData from '~/hooks/useRootData';
import useUser from '~/hooks/useUser';
import braze from '~/services/braze.client';
import gtm, { LoginSource } from '~/services/gtm.client';

import type { Props } from '.';

export default function FacebookLoginButton({
  onError,
  onSuccess,
  postAuthOptions,
  pagePosition
}: Props) {
  const { t } = useTranslation('common');
  const fetcher = useFetcher<FetcherData>();
  const user = useUser();
  const env = useEnv();
  const { isBot } = useRootData();

  const errors = fetcher.data?.errors;

  function onFacebookLoginSuccess(res: LoginResponse['authResponse']) {
    fetcher.submit(
      {
        action: postAuthOptions?.action ?? '',
        guestId: user.guestId ?? '',
        productId: postAuthOptions?.productId ?? '',
        redirectUrl: postAuthOptions?.redirectUrl ?? '',
        sizes: JSON.stringify(postAuthOptions?.sizes ?? []),
        token: res?.accessToken ?? ''
      },
      { action: '/bolt/login/facebook', method: 'post' }
    );

    gtm.trackedButtonClick({
      buttonName: 'SIGN_IN_FACEBOOK',
      pagePosition
    });
  }

  const onSignIn = useCallback(() => {
    gtm.sendLoginEvent(user.id, LoginSource.Meta);

    braze.setUser(user.id);
    braze.logLoginEvent();

    onSuccess?.();
  }, [onSuccess, user.id]);

  useEffect(() => {
    if (errors?.message) onError(errors.message);
  }, [errors, onError]);

  useEffect(() => {
    if (fetcher.data?.success) onSignIn();
  }, [fetcher.data, onSignIn]);

  const isLoggingIn = fetcher.state === 'submitting';

  function handleFacebookButtonClick() {
    new RegisterIntentEvent({
      buttonName: 'SIGN_IN_FACEBOOK',
      isBot,
      pagePosition: pagePosition || ''
    }).sendToGtm();
  }

  return (
    <ClientOnly>
      {() => (
        <div className="flex h-12 w-full items-center justify-center rounded-lg bg-concrete px-4 py-2 text-black">
          <ReactFacebookLogin
            appId={env.FACEBOOK_APP_ID}
            onSuccess={onFacebookLoginSuccess}
            fields="id,email"
            onFail={() =>
              onError(t('login.socialLoginError', { name: 'Facebook' }))
            }
            render={({ onClick }) => (
              <button
                onClick={() => {
                  handleFacebookButtonClick();
                  if (onClick) onClick();
                }}
                style={{
                  alignItems: 'center',
                  display: 'flex',
                  fontSize: '16px',
                  fontWeight: '600',
                  gap: '10px',
                  justifyContent: 'center',
                  pointerEvents: isLoggingIn ? 'none' : 'auto',
                  width: '100%'
                }}
              >
                {isLoggingIn ? (
                  'Signing in...'
                ) : (
                  <>
                    <FacebookIcon height="24" width="24" />
                    <span className="hidden md:inline-block">Facebook</span>
                  </>
                )}
              </button>
            )}
          />
        </div>
      )}
    </ClientOnly>
  );
}
